/**
 * Shared bits used across every Settings tab — section header, status
 * dot, breadcrumbs, confirm modal. All ported from
 * `oneads-handoff_settings/oneads/project/src/screens/settings.jsx`
 * with the same class names + spacing so the tabs stay consistent.
 */

import { useEffect, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import type { ReactNode } from "react";
import { Btn } from "@/components/Btn";

export function StSectionHead({
  title,
  desc,
  right,
}: {
  title: string;
  desc?: string;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 20,
        marginBottom: 18,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h2>
        {desc && (
          <div
            className="muted"
            style={{
              fontSize: 13,
              marginTop: 6,
              lineHeight: 1.5,
              maxWidth: 720,
            }}
          >
            {desc}
          </div>
        )}
      </div>
      {right && <div style={{ flex: "0 0 auto" }}>{right}</div>}
    </div>
  );
}

export function StStatusDot({
  kind,
}: {
  kind: "ok" | "warn" | "err" | "off";
}) {
  const c =
    kind === "ok"
      ? "var(--c-pos-500)"
      : kind === "warn"
        ? "var(--c-warn-500, #d97706)"
        : kind === "err"
          ? "var(--c-neg-500)"
          : "var(--c-text-4)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12.5,
        color: "var(--c-text-2)",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: c,
          boxShadow: kind === "ok" ? `0 0 0 3px ${c}25` : "none",
        }}
      />
      {kind === "ok" && "Running"}
      {kind === "warn" && "Pending"}
      {kind === "err" && "Error"}
      {kind === "off" && "Not connected"}
    </span>
  );
}

export function StConfirmModal({
  open,
  title,
  body,
  onCancel,
  onConfirm,
  danger,
  doubleConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  onCancel: () => void;
  onConfirm: () => void;
  danger?: boolean;
  doubleConfirm?: boolean;
}) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);
  useLayer(open, onCancel);
  if (!open) return null;
  const final = !doubleConfirm || step === 1;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,30,.45)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          background: "var(--c-surface)",
          borderRadius: 14,
          padding: 22,
          boxShadow: "var(--sh-3)",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
        <div
          className="muted"
          style={{ fontSize: 13, marginTop: 10, lineHeight: 1.5 }}
        >
          {doubleConfirm && step === 1
            ? "This action is permanent. Click the button again to confirm."
            : body}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 18,
          }}
        >
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn
            variant={danger ? "danger" : "primary"}
            onClick={() => {
              if (final) onConfirm();
              else setStep(1);
            }}
          >
            {danger ? (final ? "Delete" : "Continue") : "Confirm"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
